import { computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { dsDtos, dsEnums, webDtos } from '@vectronic/openApi-admin';
import { utcMoment } from '@vectronic/util';
import { cloneDeep } from 'es-toolkit';
import moment from 'moment';
import { BehaviorSubject, combineLatest, filter, map, startWith } from 'rxjs';
import { GPS_FAMILY_TYPES } from '../helpers/schedule-json.helper';
import { ScheduleCalendarCalculationService } from './schedule-calendar-calculation.service';

export interface ScheduleTypeConfig {
  hasSkipCount: boolean;
  maxSkipCount: number;
  hasFixRate: boolean;
  hasCommunicationInterval: boolean;
  hasEmptySchedule: boolean;
  isMandatory: boolean;
}

export abstract class AbstractScheduleService {
  private calendarCalculationService = inject(ScheduleCalendarCalculationService);
  abstract config: ScheduleTypeConfig;
  rulesIndexes$ = new BehaviorSubject<number[]>([0]);
  defaultRulesIndexes$ = new BehaviorSubject<number[]>([0]);
  isRemovedSchedule$ = new BehaviorSubject(false);
  isFilledAndChanged$ = new BehaviorSubject(false);
  lastIndex = 0;
  //TODO
  rules$ = new BehaviorSubject<{ [key: number]: webDtos.ScheduleRule }>({});
  defaultRules$ = new BehaviorSubject<{ [key: number]: webDtos.ScheduleRule }>({});
  fixPerTransmission$ = new BehaviorSubject<{ [key: number]: number }>({});
  scheduleType: dsEnums.ScheduleType;
  readonly isFilledAndChanged = signal(false);
  readonly forceShowCalendar = signal(false);
  readonly hasDefaultValue = signal(false);
  readonly isRemote = signal(false);
  isRuleExpanded: { [key: number]: boolean } = {};
  errors = signal<string[]>([]);
  isScheduleExpanded = signal(false);
  isValid = computed(() => !this.errors()?.length);
  rules = toSignal(this.rules$);

  isEmptyScheduleSignal = computed(
    () => Object.values(this.rules() ?? {}).length === 0 && !this.config?.isMandatory,
  );

  isFilled = computed(() => {
    if (this.isEmptyScheduleSignal()) return true;
    const rulesArray = Object.values(this.rules() ?? {});
    if (!rulesArray?.length) return false;
    return rulesArray.every((rule) => this.isRuleFilled(rule));
  });

  readonly isChanged$ = combineLatest([
    this.rules$,
    this.defaultRules$,
    this.isFilledAndChanged$,
  ]).pipe(
    map(
      ([rules, defaultRules, isFilled]) =>
        JSON.stringify(defaultRules) !== JSON.stringify(rules) ||
        (this.isEmptySchedule && isFilled),
    ),
  );

  isChanged = toSignal(this.isChanged$);

  rulesUpdatedAndFilled$ = this.rules$.pipe(
    filter((rules) => {
      if (this.isEmptySchedule) return true;
      const rulesArray = Object.values(rules);
      for (const rule of rulesArray) if (this.isRuleFilled(rule)) return true;
      return false;
    }),
    startWith(undefined),
  );

  checkValidity() {
    const rules = this.rules$.value;
    const errors = [];
    const rulesArray = Object.values(rules);
    if (
      (!rulesArray?.length && !this.isEmptySchedule) ||
      (rulesArray?.length === 1 &&
        !Object.values(rulesArray[0])?.length &&
        this.config?.isMandatory)
    )
      errors.push('There is no rule');
    if (
      rulesArray?.length &&
      Object.values(rulesArray[0])?.length &&
      this.scheduleType !== 'PROXIMITY' &&
      !this.hasRuleFromNowToThreeYears &&
      !this.isEmptySchedule
    ) {
      errors.push('There should be always a rule applied between now and in 3 years time');
    }
    rulesArray.forEach(({ periodLength, startTime, endTime }) => {
      if (periodLength > 65535 || periodLength < 0) {
        errors.push('Repeat interval is invalid');
      }
      if (
        startTime &&
        endTime &&
        !this.calendarCalculationService.isEndDateAfterStartDate(startTime, endTime)
      ) {
        errors.push('End date should be after start date');
      }
    });
    const hasEndDateInFuture = rulesArray.some((rule) =>
      utcMoment(rule.endTime).isAfter(utcMoment().add({ date: -1 })),
    );
    const hasEndDate = rulesArray.every((rule) => rule.endTime);
    if (rulesArray?.length && hasEndDate && !hasEndDateInFuture && !this.isEmptySchedule) {
      errors.push('Please select an end date in the future');
    }
    const hasFilledRules = rulesArray.some((rule) => Object.values(rule).length > 0);
    const hasCurrentRule = rulesArray.some(
      ({ startTime, endTime }) =>
        moment().isSameOrAfter(moment(startTime)) &&
        moment().isSameOrBefore(moment(endTime).endOf('date')),
    );
    if (rulesArray?.length && hasFilledRules && !hasCurrentRule) {
      errors.push('There is no rule at the current date');
    }

    this.errors.set(errors);
  }

  get hasRuleFromNowToThreeYears() {
    return this.calendarCalculationService.hasRuleFromNowToThreeYears(
      Object.values(this.rules$.value),
    );
  }

  private isRuleFilled(rule: webDtos.ScheduleRule) {
    return rule?.startTime && rule?.endTime && rule?.sequences?.length && rule?.periodLength;
  }

  private updateIsFilled(ruleIndex: number, ignoreExpand: boolean) {
    const isFilled =
      this.isValid() &&
      Object.values(this.rules$.value).every((rule) => {
        return rule && this.isRuleFilled(rule);
      });
    this.isFilledAndChanged.set(isFilled);
    this.isFilledAndChanged$.next(isFilled);
    if (!ignoreExpand) {
      this.isRuleExpanded[ruleIndex] = true;
    }
  }

  toggleRule(index: number) {
    this.isRuleExpanded[index] = !this.isRuleExpanded[index];
  }

  addDefaultSequenceToRule(ruleIndex: number) {
    const rules = cloneDeep(this.rules$.value);
    const rule: webDtos.ScheduleRule = rules[ruleIndex] ?? {};
    if (!rule.sequences) rule.sequences = [];
    const periodLength = rule.periodLength || 24 * 60;
    if (this.scheduleType === 'VHF') {
      rule.sequences.push({ fixRate: undefined, duration: periodLength, offset: 0 });
    } else if (!this.config?.hasFixRate) {
      rule.sequences.push({ fixRate: 120 * 60, duration: periodLength, offset: 0 });
    } else if (periodLength === 10080) {
      rule.sequences.push({ fixRate: 120 * 60, duration: 10080, offset: 0 });
    } else {
      rule.sequences.push({ fixRate: 120 * 60, duration: periodLength || 24 * 60, offset: 0 });
    }
    this.updateRule(ruleIndex, rule, false);
  }

  onClickAddRule() {
    this.lastIndex++;
    const rulesIndexes = this.rulesIndexes$.value;
    rulesIndexes.push(this.lastIndex);
    this.rulesIndexes$.next(rulesIndexes);
    this.rules$.next({ ...this.rules$.value, [this.lastIndex]: {} });
    this.isRuleExpanded[this.lastIndex] = true;
    this.updateIsFilled(this.lastIndex, true);
  }

  onRemoveRule(index: number, readonly: boolean) {
    if (readonly) {
      return;
    }
    const rules = cloneDeep(this.rules$.value);
    const rulesArray = Object.values(rules);

    if (rulesArray.length <= 1 && !this.isRemote() && !this.config?.isMandatory) {
      this.setEmptySchedule();
      return;
    }

    if (rulesArray.length <= 1 && (this.isRemote() || this.config?.isMandatory)) {
      this.onRemoveSchedule();
      return;
    }

    const newRules: { [key: number]: webDtos.ScheduleRule } = {};
    const newFixPerTransmission: { [key: number]: number } = {};
    const newIsRuleExpanded: { [key: number]: boolean } = {};
    const newRulesIndexes: number[] = [];
    const oldFixPerTransmission = this.fixPerTransmission$.value;

    this.rulesIndexes$.value.forEach((oldIdx) => {
      if (oldIdx !== index) {
        const newIdx = newRulesIndexes.length;
        newRules[newIdx] = rules[oldIdx];
        if (oldFixPerTransmission[oldIdx] !== undefined) {
          newFixPerTransmission[newIdx] = oldFixPerTransmission[oldIdx];
        }
        if (this.isRuleExpanded[oldIdx] !== undefined) {
          newIsRuleExpanded[newIdx] = this.isRuleExpanded[oldIdx];
        }
        newRulesIndexes.push(newIdx);
      }
    });

    this.rules$.next(newRules);
    this.fixPerTransmission$.next(newFixPerTransmission);
    this.isRuleExpanded = newIsRuleExpanded;
    this.rulesIndexes$.next(newRulesIndexes);
    this.lastIndex = newRulesIndexes.length - 1;
    this.updateIsFilled(this.lastIndex, true);
  }

  setEmptySchedule() {
    this.isScheduleExpanded.set(false);
    this.isFilledAndChanged.set(true);
    this.isFilledAndChanged$.next(true);
    this.rules$.next({});
    this.rulesIndexes$.next([]);
    this.lastIndex = -1;
    this.fixPerTransmission$.next({});
  }

  get isEmptySchedule() {
    const rules = Object.values(this.rules$.value);
    return rules?.length === 0 && !this.config?.isMandatory;
  }

  updateRule(index: number, rule: webDtos.ScheduleRule, readonly: boolean) {
    if (readonly) {
      return;
    }
    const rules = cloneDeep(this.rules$.value);
    const cleanedRule = cloneDeep(rule);
    if (!this.config?.hasSkipCount) {
      if (cleanedRule && 'skipCount' in cleanedRule) {
        delete cleanedRule.skipCount;
      }
    }
    rules[index] = cleanedRule;
    this.rules$.next(rules);
    this.updateIsFilled(index, readonly);
  }

  updateSequence(
    ruleIndex: number,
    sequenceIndex: number,
    { fixRate, duration, offset }: webDtos.ScheduleSequence,
    readonly: boolean,
  ) {
    if (readonly) {
      return;
    }
    const rules = cloneDeep(this.rules$.value);
    const oldSequence = rules[ruleIndex].sequences[sequenceIndex];
    oldSequence.fixRate = isNaN(fixRate) ? undefined : fixRate;
    oldSequence.duration = duration;
    oldSequence.offset = offset;
    this.rules$.next(rules);
    this.updateIsFilled(ruleIndex, readonly);
  }

  updateSequenceOffsetDuration(
    ruleIndex: number,
    sequenceIndex: number,
    offset: number,
    duration: number,
    readonly: boolean,
  ) {
    if (readonly) {
      return;
    }
    const rules = cloneDeep(this.rules$.value);
    const oldSequence = rules[ruleIndex].sequences[sequenceIndex];
    if (offset !== oldSequence.offset || duration !== oldSequence.duration) {
      oldSequence.offset = offset;
      oldSequence.duration = duration;
      this.rules$.next(rules);
    }
    this.updateIsFilled(ruleIndex, readonly);
  }

  get isOneRuleExpanded() {
    return Object.values(this.isRuleExpanded).includes(true);
  }

  onRemoveSchedule() {
    this.isFilledAndChanged.set(false);
    this.hasDefaultValue.set(false);
    this.isFilledAndChanged$.next(false);
    this.isScheduleExpanded.set(false);
    this.isRemovedSchedule$.next(true);
    this.isRemovedSchedule$.next(false);
    this.rules$.next({ 0: {} });
    this.rulesIndexes$.next([0]);
    this.isRuleExpanded = {};
    this.lastIndex = 0;
    this.fixPerTransmission$.next({});
  }

  removeDefaultSchedule() {
    this.defaultRules$.next({ 0: {} });
    this.defaultRulesIndexes$.next([0]);
  }

  setToDefaultSchedule() {
    if (Object.values(this.defaultRules$.value).length) {
      this.rules$.next(cloneDeep(this.defaultRules$.value));
      this.rulesIndexes$.next(cloneDeep(this.defaultRulesIndexes$.value));
    }
    this.isFilledAndChanged$.next(false);
    this.isFilledAndChanged.set(false);
    if (
      Object.values(this.defaultRules$.value).length &&
      Object.values(this.defaultRules$.value[0]).length
    ) {
      this.isScheduleExpanded.set(true);
      this.rulesIndexes$.value.forEach((index) => (this.isRuleExpanded[index] = true));
    } else {
      this.isScheduleExpanded.set(false);
    }
  }

  onAddSchedule() {
    if (this.isEmptySchedule) this.onRemoveSchedule();
    this.isScheduleExpanded.set(true);
    this.isRuleExpanded[0] = true;
  }

  initializeRule(
    ruleIndex: number,
    permanentStartDate = '2000-01-01',
    permanentEndDate = '2050-01-01',
  ): void {
    const rule: webDtos.ScheduleRule = {
      startTime: permanentStartDate,
      endTime: permanentEndDate,
      skipCount: 0,
      periodLength: 24 * 60,
      sequences: [],
    };
    this.updateRule(ruleIndex, rule, false);
  }

  loadRules(rules: webDtos.ScheduleRule[]) {
    if (!rules) return;
    const rulesObj: { [key: number]: webDtos.ScheduleRule } = {};
    rules.forEach((rule, index) => {
      rulesObj[index] = rule;
      this.isRuleExpanded[index] = true;
    });
    this.rules$.next(rulesObj);
    this.rulesIndexes$.next(Object.keys(rulesObj).map(Number));
    this.lastIndex = rules.length - 1;
    this.isScheduleExpanded.set(true);
    this.isFilledAndChanged.set(true);
    this.isFilledAndChanged$.next(true);
    this.updateIsFilled(this.lastIndex, true);
  }

  toSchedule(): dsDtos.Schedule {
    const scheduleType = GPS_FAMILY_TYPES.has(this.scheduleType) ? 'GPS' : this.scheduleType;
    return {
      scheduleType,
      userDefined: true,
      rules: Object.values(this.rules$.value).filter(Boolean) as dsDtos.ScheduleRule[],
    };
  }
}
